import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, Observable } from 'rxjs';
import {
  DynamicDialogRef,
  DynamicDialogConfig,
  DialogService,
} from 'primeng/dynamicdialog';
import {
  IntegrationEntityType,
  IntegrationField,
  IntegrationFieldDataType,
  IntegrationSystem,
} from 'src/app/shared/models/integration.models';
import { IntegrationService } from 'src/app/shared/services/integration.service';
import { AppToastService } from 'src/app/shared/services/app-toast.service';

@Component({
  selector: 'app-integration-field-modal',
  templateUrl: './integration-field-modal.component.html',
  standalone: false,
  providers: [DialogService],
})
export class IntegrationFieldModalComponent implements OnInit {
  editingId?: number;
  saving = false;

  systemOptions: Array<IntegrationSystem & { label: string }> = [];
  entityTypeOptions: IntegrationEntityType[] = [];

  readonly dataTypeOptions: Array<{ label: string; value: IntegrationFieldDataType }> = [
    { label: 'Text', value: 'text' },
    { label: 'Number', value: 'number' },
    { label: 'Date', value: 'date' },
    { label: 'Boolean', value: 'boolean' },
  ];

  form: FormGroup = this.fb.group({
    integrationSystemId: [null, Validators.required],
    entityType: ['', Validators.required],
    externalKey: ['', Validators.required],
    label: ['', Validators.required],
    description: [''],
    dataType: ['text' as IntegrationFieldDataType, Validators.required],
    required: [false],
    visibleInForm: [true],
    editable: [true],
    isUnique: [false],
    enabled: [true],
    sortOrder: [0],
    validationRegex: [''],
  });

  constructor(
    private readonly fb: FormBuilder,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private readonly integrationService: IntegrationService,
    private readonly toastService: AppToastService,
  ) {}

  ngOnInit(): void {
    const field: IntegrationField | undefined = this.config.data?.field;
    const systems: IntegrationSystem[] = this.config.data?.systems ?? [];
    const entityTypes: IntegrationEntityType[] = this.config.data?.entityTypes ?? [];
    const selectedSystemCode: string = this.config.data?.selectedSystemCode ?? '';

    this.systemOptions = systems.map((s) => ({
      ...s,
      label: `${s.tenantName ? s.tenantName + ' - ' : ''}${s.name} (${s.code})`,
    }));
    this.entityTypeOptions = entityTypes;

    this.editingId = field?.id;

    const defaultSystem =
      systems.find((s) => s.id === field?.integrationSystemId) ||
      systems.find((s) => s.code === selectedSystemCode) ||
      systems.find((s) => s.code === 'finpac_middleware') ||
      systems[0];

    this.form.reset({
      integrationSystemId: field?.integrationSystemId || defaultSystem?.id || null,
      entityType: field?.entityType || entityTypes[0]?.code || '',
      externalKey: field?.externalKey || '',
      label: field?.label || '',
      description: field?.description || '',
      dataType: field?.dataType || 'text',
      required: field?.required ?? false,
      visibleInForm: field?.visibleInForm ?? true,
      editable: field?.editable ?? true,
      isUnique: field?.isUnique ?? false,
      enabled: field?.enabled ?? true,
      sortOrder: field?.sortOrder ?? 0,
      validationRegex: field?.validationRegex || '',
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const raw = this.form.getRawValue();
    const payload = {
      integrationSystemId: Number(raw.integrationSystemId),
      entityType: String(raw.entityType || '').trim(),
      externalKey: String(raw.externalKey || '').trim(),
      label: String(raw.label || '').trim(),
      description: raw.description ? String(raw.description).trim() : null,
      dataType: raw.dataType,
      required: !!raw.required,
      visibleInForm: !!raw.visibleInForm,
      editable: !!raw.editable,
      isUnique: !!raw.isUnique,
      enabled: !!raw.enabled,
      sortOrder: Number(raw.sortOrder || 0),
      validationRegex: raw.validationRegex ? String(raw.validationRegex).trim() : null,
    };

    const request$: Observable<unknown> = this.editingId
      ? this.integrationService.updateField(this.editingId, payload)
      : this.integrationService.addField(payload);

    request$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: () => {
        this.toastService.success(
          this.editingId ? 'Field updated' : 'Field created',
          'Integration field saved successfully.',
        );
        this.ref.close('success');
      },
    });
  }
}
